import {useState, Dispatch, SetStateAction, memo} from 'react'
import {useParams} from 'react-router-dom'
import {differenceInCalendarMonths, parseISO} from 'date-fns'
import {Column} from '@ant-design/plots'
import {Space} from 'antd'

import styles from './ProjectForecastChart.module.scss'
import {
  Box,
  Text,
  Button,
  Circle,
  IconStat,
  TargetGoalIcon,
} from 'common/components'
import {useGetForecast} from 'common/api'
import {
  fixNumber,
  lastUpdatedItemFromArray,
  formatDate,
  totalNumberFromArray,
  arrayFromMonthsCount,
  isSameYearMonth,
  createReport,
  capitalizeFirstLetter,
  isItemRepayment,
  isEqual,
} from 'common/helpers'
import {COLOR, CHART_THEME} from 'common/constants'
import {ForecastItem} from 'common/types'
import {useGetProject} from '..'

interface ChangeRepaymentsViewProps {
  label: string
  setRepaymentsView: Dispatch<SetStateAction<boolean>>
}

interface ChartProps {
  isRepaymentsView: boolean
  forecast: ForecastItem[]
  openDate: Date
}

interface ChartDataValue {
  type: string
  month: string
  amount: number
}

const getReportData = (data: any[]) =>
  data.map((item, index) => ({
    '#': index + 1,
    'Forecast type': capitalizeFirstLetter(item.type),
    Date: formatDate(item.date),
    Amount: item.value.amount,
    'Created by': `${item.value.createdBy.firstName} ${item.value.createdBy.lastName}`,
    'Transaction amount': item.transactions?.length
      ? totalNumberFromArray(item.transactions, 'amount')
      : null,
    'Transaction date': item.transactions?.length
      ? formatDate(item.transactions[0].date)
      : null,
    'Forecasted amount': item.rating ? item.rating.forecastedAmount : null,
    'Processed amount': item.rating ? item.rating.processedAmount : null,
    Points: item.rating ? item.rating.points : null,
    Variance: item.rating ? item.rating.variance : null,
  }))

const options = {
  theme: CHART_THEME,
  height: 200,
  isGroup: true,
  isStack: true,
  legend: false,
  seriesField: 'type',
  xField: 'month',
  yField: 'amount',
  dodgePadding: 8,
  maxColumnWidth: 12,
  xAxis: {
    tickLine: false,
    line: false,
    grid: {
      line: {
        style: {
          stroke: COLOR.grey,
          lineDash: [1.5, 4],
        },
      },
    },
  },
  tooltip: {
    formatter: ({type}: any) => ({
      name: type,
    }),
  },
}

const getChartValue = (type: string, month: string, amount: number = 0) => ({
  type,
  month,
  amount,
})

const Chart = memo(
  ({isRepaymentsView, forecast, openDate}: ChartProps) => {
    const valueType = isRepaymentsView ? 'Repayment' : 'Drawdown'
    const lastDate = parseISO(forecast.at(-1).value.date)
    const months = differenceInCalendarMonths(lastDate, openDate) + 1
    const monthsArray = arrayFromMonthsCount(months, openDate)
    const items = forecast.filter(
      (item) => item.type === (isRepaymentsView ? 'repayment' : 'drawdown')
    )

    const data: ChartDataValue[][] = []
    monthsArray.forEach((date) => {
      const month = formatDate(date, 'fourth')
      if (items.some((item) => isSameYearMonth(item.date, date))) {
        const currentItems = items.filter((item) =>
          isSameYearMonth(item.date, date)
        )
        const fakeDrawdown = currentItems.find((item) => item.isFake)
        if (fakeDrawdown) {
          currentItems.push({
            ...fakeDrawdown,
            transactions: [
              {
                amount: fakeDrawdown.value.amount,
              },
            ],
          })
        }
        const values = currentItems.map((drawdown) =>
          getChartValue(
            drawdown.transactions?.length ? valueType : 'Forecast',
            month,
            drawdown.transactions?.length
              ? totalNumberFromArray(drawdown.transactions, 'amount')
              : drawdown.value.amount
          )
        )
        if (values.every((value) => value.type !== valueType)) {
          values.push(getChartValue(valueType, month))
        }
        if (values.every((value) => value.type !== 'Forecast')) {
          values.push(getChartValue('Forecast', month))
        }
        return data.push(values)
      }
      const values = [
        getChartValue('Forecast', month),
        getChartValue(valueType, month),
      ]
      data.push(values)
    })
    const chartData = data.flat()

    const maxAmount = Math.max(...chartData.map((item) => item.amount))

    const hasTransactions = items.some((item) =>
      Boolean(item.transactions?.length)
    )

    const config: any = {
      ...options,
      data: chartData,
      color: hasTransactions
        ? [COLOR.coal80, isRepaymentsView ? COLOR.green : COLOR.turquoize]
        : [isRepaymentsView ? COLOR.green : COLOR.turquoize, COLOR.coal80],
      yAxis: {
        tickInterval: maxAmount / 2 + maxAmount * 0.1,
        label: {
          offsetY: -10,
          style: {
            fontSize: 10,
            fill: COLOR.coal,
          },
          formatter: (value: number) => Math.round(value / 1000) + ' k',
        },
        tickLine: false,
        grid: {
          line: {
            style: {
              stroke: COLOR.mist,
            },
          },
        },
      },
      columnStyle: (value: ChartDataValue) => ({
        radius: 6,
        stroke: value.amount
          ? value.type === 'Forecast'
            ? COLOR.turquoizeDeep
            : isRepaymentsView
            ? COLOR.greenDeep
            : COLOR.coal
          : undefined,
      }),
    }

    return <Column {...config} />
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
)

const ChangeRepaymentsView = ({
  label,
  setRepaymentsView,
}: ChangeRepaymentsViewProps) => {
  const handleRepaymentsViewToggle = () => setRepaymentsView((state) => !state)

  return (
    <Button variant="secondary" onClick={handleRepaymentsViewToggle}>
      view {label}
    </Button>
  )
}

export default function ProjectForecastChart() {
  const params = useParams()
  const {data: project} = useGetProject(params.uuid)
  const {data: forecast} = useGetForecast(params.uuid)
  const [isRepaymentsView, setRepaymentsView] = useState(false)

  const handleReport = () =>
    createReport(
      getReportData(forecast),
      `Project forecast`,
      `project-forecast-report`
    )

  const hasRepayments = forecast.some((item) => isItemRepayment(item))

  const lastUpdatedItem = lastUpdatedItemFromArray(forecast)
  const updatedBy = lastUpdatedItem?.value.createdBy
  const forecastRatings = forecast
    .filter((item) => item.rating)
    .map((item) => item.rating)
  const averagePoints =
    totalNumberFromArray(forecastRatings, 'points') / forecastRatings.length ||
    0
  const averageVariance =
    totalNumberFromArray(forecastRatings, 'variance') /
      forecastRatings.length || 0

  return (
    <div className={styles.root}>
      <div className={styles.block}>
        {lastUpdatedItem && (
          <Text weight="medium">
            Last forecast updated {formatDate(lastUpdatedItem.updatedAt)} by{' '}
            {updatedBy.firstName} {updatedBy.lastName}
          </Text>
        )}
        <IconStat
          icon={<TargetGoalIcon />}
          iconColor="turquoize"
          value={
            <>
              {fixNumber(averagePoints)}{' '}
              <Text as="span" color="grey" size="2xl">
                {fixNumber(averageVariance)}% variance
              </Text>
            </>
          }
          label="Project points for forecast performance"
        />
      </div>
      <div>
        <div className={styles.panel}>
          <Box className={styles.legend} weight="medium">
            <Space>
              <Circle border color="turquoize" />
              Forecast
            </Space>
            <Space>
              <Circle
                border
                color={isRepaymentsView ? 'darkGreen' : 'darkGrey'}
              />
              <span className={styles.type}>
                {isRepaymentsView ? 'repayments' : 'drawdowns'}
              </span>
              {hasRepayments && (
                <ChangeRepaymentsView
                  label={isRepaymentsView ? 'drawdowns' : 'repayments'}
                  setRepaymentsView={setRepaymentsView}
                />
              )}
            </Space>
          </Box>
          <Button disabled={!forecast.length} onClick={handleReport}>
            Create report
          </Button>
        </div>
        <Chart
          isRepaymentsView={isRepaymentsView}
          forecast={forecast}
          openDate={parseISO(project.openDate)}
        />
      </div>
    </div>
  )
}
